import React, { useState, useEffect, useMemo, useRef } from 'react'
import toast from 'react-hot-toast'
import { useAuth } from '../../contexts/AuthContext'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { SelectDropdown } from '../../components/SelectDropdown'
import {
  HomeIcon,
  ClockIcon,
  CogIcon,
  GlobeAltIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  MapPinIcon,
  PencilIcon,
  PlusIcon,
  CheckCircleIcon,
  XCircleIcon,
  TrashIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  HashtagIcon,
  ExclamationTriangleIcon,
  ServerStackIcon
} from '@heroicons/react/24/outline'
import VenuePsgGatewayModal from './VenuePsgGatewayModal'
import ImportMappingPreviewModal from './ImportMappingPreviewModal'
import ImportMappingTemplateModal, {
  IMPORT_MAPPING_TYPE_OPTIONS,
  ImportMappingReadonlyTable,
  emptyPipeMapping,
  ensurePipeMapping,
  sourceFormatLabel,
} from './ImportMappingTemplateModal'
import {
  downloadMachineMasterTemplate,
  downloadMachineMasterSample,
  downloadImportMappingSample,
  importMappingSampleLabel,
} from '../../utils/importSampleTemplates'
import { resolveSourceLabelToColumn } from '../../utils/igtPatronTemplate'

/** Next-number preview; token rules match backend `serialNumbers.js` / docs/SERIAL_NUMBER_SYSTEM.md */
function previewSerialPattern(pattern, currentValue, venueCode = '') {
  if (!pattern) return ''
  const nextVal = (Number(currentValue) || 0) + 1
  const now = new Date()
  const yyyy = String(now.getFullYear())
  const yy = yyyy.slice(2)
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const dd = String(now.getDate()).padStart(2, '0')
  let result = pattern
  result = result.replace(/\{YYYY\}/gi, yyyy)
  result = result.replace(/\{YY\}/gi, yy)
  result = result.replace(/\{MM\}/gi, mm)
  result = result.replace(/\{DD\}/gi, dd)
  result = result.replace(/\{YYMMDD\}/gi, `${yy}${mm}${dd}`)
  result = result.replace(/\{YYYYMMDD\}/gi, `${yyyy}${mm}${dd}`)
  const vc = String(venueCode || '').trim()
  result = result.replace(/\{venue\}/gi, vc || '{venue}')
  result = result.replace(/\{seq:(\d+)\}/gi, (_, digits) =>
    String(nextVal).padStart(parseInt(digits, 10), '0')
  )
  return result
}

/** RXConnect / gateway DimMachine_* pipe layout (column index → label). Reference for pipe-delimited txt.
 * Col0 = machine_pk (matches Meter_* col0). Col1 is an alternate id — do not use for meter remap.
 * Col14 = machine_class (EGM normal | MTGM multi).
 */
const EGM_DIMMACHINE_PIPE_SOURCE_LABELS = {
  0: 'machine_pk',
  1: 'alt_machine_id',
  2: 'install_date',
  3: 'deinstall_date',
  7: 'egm_code',
  8: 'manufacturer',
  10: 'serial_number',
  11: 'game_name',
  13: 'status',
  14: 'machine_class',
  15: 'denom',
  16: 'max_bet',
  17: 'rtp',
  36: 'site_name',
  37: 'site_code',
  42: 'area',
  43: 'zone',
}

const EGM_DIMMACHINE_PIPE_MAPPINGS = {
  EgmPK: 'egm_code',
  EgmId: 'machine_pk',
  HouseNumber: 'egm_code',
  EgmName: 'game_name',
  GameName: 'game_name',
  SerialNumber: 'serial_number',
  Manufacturer: 'manufacturer',
  MachineClass: 'machine_class',
  Denom: 'denom',
  MaxBet: 'max_bet',
  RTP: 'rtp',
  Zone: 'zone',
  Area: 'area',
  Status: 'status',
  InstallDate: 'install_date',
  DeinstallDate: 'deinstall_date',
  Venue: 'site_name',
  VenueCode: 'site_code',
}

function importMappingFromTemplateContent(c) {
  if (!c || typeof c !== 'object') return null
  return ensurePipeMapping({
    venueCode: c.venueCode,
    skipRows: c.skipRows,
    sourceFormat: c.sourceFormat,
    filePrefix: c.filePrefix,
    sourceLabels: c.sourceLabels ? { ...c.sourceLabels } : {},
    mappings: { ...(c.mappings || {}) },
  })
}

const IMPORT_MAPPING_TYPE_TO_CONFIG_KEY = {
  meter: 'meter_template_id',
  sessions: 'sessions_template_id',
  egm: 'egm_template_id',
  patron: 'patron_template_id',
  slot_analysis: 'slot_analysis_template_id',
  visitor: 'visitor_template_id',
}

function tenantDefaultTemplateIdsFromImportConfig(importConfig) {
  if (!importConfig) {
    return { meter: '', sessions: '', egm: '', patron: '', slot_analysis: '', visitor: '' }
  }
  return {
    meter: importConfig.meter_template_id != null ? String(importConfig.meter_template_id) : '',
    sessions: importConfig.sessions_template_id != null ? String(importConfig.sessions_template_id) : '',
    egm: importConfig.egm_template_id != null ? String(importConfig.egm_template_id) : '',
    patron: importConfig.patron_template_id != null ? String(importConfig.patron_template_id) : '',
    slot_analysis: importConfig.slot_analysis_template_id != null ? String(importConfig.slot_analysis_template_id) : '',
    visitor: importConfig.visitor_template_id != null ? String(importConfig.visitor_template_id) : '',
  }
}

async function saveTenantDefaultImportTemplateId(importMappingType, templateId) {
  const configKey = IMPORT_MAPPING_TYPE_TO_CONFIG_KEY[importMappingType]
  if (!configKey) throw new Error('Unknown import mapping type')
  const res = await authFetch(getLocalApiUrl('/api/admin/import/config'), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ [configKey]: parseInt(String(templateId), 10) }),
  })
  const j = await res.json().catch(() => ({}))
  if (!res.ok || !j.success) throw new Error(j.error || j.detail || 'Failed to save default template')
  return j.data
}

/**
 * Build save/template payload — column numbers with chosen delimiter (sourceFormat).
 */
function normalizeImportMappingForSave(cfg) {
  if (!cfg) return null
  const pipeCfg = ensurePipeMapping(cfg)
  const sourceLabels = pipeCfg.sourceLabels || {}
  const mappings = {}
  for (const [field, source] of Object.entries(pipeCfg.mappings || {})) {
    if (source == null || String(source).trim() === '') continue
    const src = String(source).trim()
    if (src.startsWith('derive:')) {
      const parts = src.slice(7).split(',').map((p) => p.trim()).filter(Boolean)
      const resolved = parts.map((p) => {
        const col = resolveSourceLabelToColumn(p, sourceLabels)
        return col != null ? String(col) : p
      })
      mappings[field] = `derive:${resolved.join(',')}`
      continue
    }
    const col = resolveSourceLabelToColumn(src, sourceLabels)
    mappings[field] = col != null ? String(col) : src
  }
  const payload = {
    sourceFormat: pipeCfg.sourceFormat,
    mappings,
  }
  if (pipeCfg.venueCode) payload.venueCode = pipeCfg.venueCode
  if (pipeCfg.skipRows != null) payload.skipRows = pipeCfg.skipRows
  const filePrefix = String(pipeCfg.filePrefix || '').trim()
  if (filePrefix) payload.filePrefix = filePrefix
  if (Object.keys(sourceLabels).length) payload.sourceLabels = sourceLabels
  return payload
}

function downloadBrowserFile(filename, content, mimeType) {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function templateContentToPayload(cfg) {
  return normalizeImportMappingForSave(cfg)
}

function downloadImportMappingJson(filename, cfg) {
  const payload = templateContentToPayload(cfg)
  if (!payload) return
  downloadBrowserFile(filename, JSON.stringify(payload, null, 2), 'application/json')
}

function sanitizeDownloadBasename(name) {
  return String(name || 'mapping')
    .trim()
    .replace(/[^\w.\-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 80) || 'mapping'
}

/** Prefer loaded template name in the download filename when available. */
function importMappingJsonFilename(importType, templateName) {
  const base = templateName ? sanitizeDownloadBasename(templateName) : String(importType || 'mapping')
  return `${base}-${importType}-mapping.json`
}

function defaultSaveTemplateTypeForMapping(importMappingType) {
  if (importMappingType === 'meter') return 'metering'
  if (importMappingType === 'patron') return 'patron'
  if (importMappingType === 'slot_analysis') return 'slot_analysis'
  if (importMappingType === 'visitor') return 'visitor'
  return 'gaming'
}

function suggestedTemplateNameFromUploadFile(fileName, importMappingType) {
  const raw = String(fileName || '')
    .replace(/\.json$/i, '')
    .replace(/-import-mapping$/i, '')
    .replace(/-mapping$/i, '')
    .replace(new RegExp(`-${importMappingType}$`, 'i'), '')
    .trim()
  return raw || `${importMappingType}-upload`
}

/** Parse JSON exported via Download mapping JSON (or saved template_content). */
function parseUploadedImportMappingJson(text) {
  let raw
  try {
    raw = JSON.parse(text)
  } catch {
    throw new Error('File is not valid JSON.')
  }
  if (!raw || typeof raw !== 'object') {
    throw new Error('Mapping JSON must be an object.')
  }
  const content = raw.template_content && typeof raw.template_content === 'object'
    ? raw.template_content
    : raw
  if (!content.mappings || typeof content.mappings !== 'object') {
    throw new Error('Missing mappings object — use a file from Download mapping JSON.')
  }
  const loaded = importMappingFromTemplateContent(content)
  if (!loaded) throw new Error('Could not read mapping from file.')
  return loaded
}

const EGM_COLUMN_SCHEMA_NOTES = {
  machine_pk: 'DimMachine col0 — same id as Meter_* col0 (required for meter→house remap)',
  alt_machine_id: 'DimMachine col1 alternate id — do not use for meter remap',
  egm_code: 'House / floor number — maps to EgmPK (required)',
  serial_number: 'Cabinet serial — maps to SerialNumber (DimMachine col 10)',
  install_date: 'Install timestamp',
  deinstall_date: 'Deinstall timestamp if removed',
  game_name: 'Game title on machine',
  manufacturer: 'OEM',
  status: 'e.g. Active, Inactive',
  denom: 'Credit denomination (decimal string)',
  max_bet: 'Max bet credits',
  rtp: 'Return to player %',
  site_name: 'Venue display name',
  site_code: 'Short venue / site code from extract',
  area: 'Floor area label',
  zone: 'Zone / bank label',
}

function downloadEgmColumnSchemaCsv() {
  const labelToCol = {}
  for (const [idx, label] of Object.entries(EGM_DIMMACHINE_PIPE_SOURCE_LABELS)) {
    labelToCol[label] = idx
  }
  const lines = [
    'column_index,source_label,psg_field,required,notes',
  ]
  const seen = new Set()
  for (const [psg, label] of Object.entries(EGM_DIMMACHINE_PIPE_MAPPINGS)) {
    const col = labelToCol[label] ?? ''
    const required = psg === 'EgmPK' ? 'yes' : ''
    const notes = (EGM_COLUMN_SCHEMA_NOTES[label] || '').replace(/,/g, ';')
    lines.push(`${col},${label},${psg},${required},${notes}`)
    seen.add(label)
  }
  for (const [idx, label] of Object.entries(EGM_DIMMACHINE_PIPE_SOURCE_LABELS)) {
    if (seen.has(label)) continue
    const notes = (EGM_COLUMN_SCHEMA_NOTES[label] || '').replace(/,/g, ';')
    lines.push(`${idx},${label},,,"${notes}"`)
  }
  lines.push('')
  lines.push('# DimMachine_* — pipe-delimited txt, no header, ~65 columns (RXConnect / gateway extracts)')
  lines.push('# Import mapping JSON: Group Config → Import Mapping → EGM → Download mapping JSON')
  downloadBrowserFile('DimMachine_column_schema.csv', lines.join('\n'), 'text/csv')
}

function createEmptyGroupData() {
  return {
    groupInfo: {
      groupName: '',
      gsid: '',
      groupCode: '',
      timezone: '',
      timezoneAbbrev: '',
      currency: '',
      jurisdiction: '',
      established: '',
      headquarters: '',
      status: '',
      membershipSystem: '',
      visitorManagementSystem: '',
      gamingSystem: '',
      totalEGMs: null,
      totalMTGMs: null,
    },
    venues: [],
    groupSettings: {
      defaultLimits: {},
      complianceSettings: {},
      reportingConfig: {},
    },
  }
}

/** Merge optional persisted profile blob from system_configuration (GROUP_UI_PROFILE_JSON). */
function mergeGroupProfileFromConfigCategories(base, categories) {
  if (!Array.isArray(categories)) return
  const groupCat = categories.find((c) => c.category === 'Group' || c.category === 'Group Configuration')
  const byName = Object.fromEntries((groupCat?.settings || []).map((s) => [s.name, s.value]))
  const raw = byName.GROUP_UI_PROFILE_JSON || byName.GROUP_PROFILE_JSON
  if (!raw || typeof raw !== 'string') return
  try {
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return
    if (parsed.groupInfo && typeof parsed.groupInfo === 'object') {
      base.groupInfo = { ...base.groupInfo, ...parsed.groupInfo }
    }
    if (parsed.groupSettings && typeof parsed.groupSettings === 'object') {
      const gs = parsed.groupSettings
      base.groupSettings = {
        defaultLimits: { ...base.groupSettings.defaultLimits, ...(gs.defaultLimits && typeof gs.defaultLimits === 'object' ? gs.defaultLimits : {}) },
        complianceSettings: { ...base.groupSettings.complianceSettings, ...(gs.complianceSettings && typeof gs.complianceSettings === 'object' ? gs.complianceSettings : {}) },
        reportingConfig: { ...base.groupSettings.reportingConfig, ...(gs.reportingConfig && typeof gs.reportingConfig === 'object' ? gs.reportingConfig : {}) },
      }
    }
  } catch {
    /* ignore invalid JSON */
  }
}

function StatusBadge({ status }) {
  const colors = {
    Active: 'bg-green-100 text-green-800',
    Inactive: 'bg-red-100 text-red-800',
    'Under Construction': 'bg-yellow-100 text-yellow-800',
    Maintenance: 'bg-blue-100 text-blue-800',
  }

  return (
    <span
      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${colors[status] || 'bg-gray-100 text-gray-800'}`}
    >
      {status}
    </span>
  )
}

function InfoCard({ title, children, icon: Icon, action, isEditing, onSave, onCancel }) {
  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Icon className="w-5 h-5 text-gray-400 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          </div>
          <div className="flex items-center space-x-2">
            {isEditing ? (
              <>
                <button
                  type="button"
                  onClick={onSave}
                  className="flex items-center px-3 py-1 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                >
                  <CheckCircleIcon className="w-3 h-3 mr-1" />
                  Save
                </button>
                <button
                  type="button"
                  onClick={onCancel}
                  className="flex items-center px-3 py-1 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                >
                  <XCircleIcon className="w-3 h-3 mr-1" />
                  Cancel
                </button>
              </>
            ) : (
              action && action
            )}
          </div>
        </div>
      </div>
      <div className="p-6">{children}</div>
    </div>
  )
}

function GroupConfiguration() {
  const { user } = useAuth()
  const [selectedTab, setSelectedTab] = useState('overview')
  const [editingGroup, setEditingGroup] = useState(false)
  const [editingVenue, setEditingVenue] = useState(null)
  const [editingTrading, setEditingTrading] = useState(false)
  const [selectedTradingVenueId, setSelectedTradingVenueId] = useState('')
  const [venueTradingDays, setVenueTradingDays] = useState({})
  const [venueHolidaySchedule, setVenueHolidaySchedule] = useState({})
  const [holidaysByVenue, setHolidaysByVenue] = useState({})
  const [eventsByVenue, setEventsByVenue] = useState({})
  const [loadingTrading, setLoadingTrading] = useState(false)
  const [editingVenueTradingId, setEditingVenueTradingId] = useState(null)
  const [tempVenueTrading, setTempVenueTrading] = useState({})
  const [savingVenueTradingId, setSavingVenueTradingId] = useState(null)
  const [tradingSaveError, setTradingSaveError] = useState('')
  const [addingVenue, setAddingVenue] = useState(false)
  const [newVenueData, setNewVenueData] = useState({})
  const [editingSettings, setEditingSettings] = useState(false)
  
  // Editable data state (hydrated from session, venue-config API, optional GROUP_UI_PROFILE_JSON)
  const [groupData, setGroupData] = useState(createEmptyGroupData)
  const [groupProfileLoading, setGroupProfileLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setGroupProfileLoading(true)
      const base = createEmptyGroupData()
      if (user?.group_id || user?.group_name) {
        base.groupInfo.groupName = user.group_name || base.groupInfo.groupName
        base.groupInfo.gsid = user.group_id || base.groupInfo.gsid
        base.groupInfo.groupCode = user.group_id || base.groupInfo.groupCode
      }
      try {
        const r = await authFetch(getLocalApiUrl('/api/venue-config/groups'))
        if (!cancelled && r.ok) {
          const j = await r.json()
          const t = j.success && Array.isArray(j.tenants) ? j.tenants[0] : null
          if (t) {
            if (t.name) base.groupInfo.groupName = t.name
            if (t.group_id) {
              base.groupInfo.gsid = t.group_id
              base.groupInfo.groupCode = t.group_id
            }
            if (t.status) base.groupInfo.status = t.status
          }
        }
      } catch {
        /* session fields remain */
      }

      try {
        const cfgRes = await authFetch(getLocalApiUrl('/api/configuration'))
        if (!cancelled && cfgRes.ok) {
          const j = await cfgRes.json()
          if (j.success && Array.isArray(j.data)) {
            mergeGroupProfileFromConfigCategories(base, j.data)
          }
        }
      } catch {
        /* optional enrichment */
      }

      if (!cancelled) {
        setGroupData(base)
        setGroupProfileLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [user?.group_id, user?.group_name])

  // DB-backed venues state
  const [venuesDb, setVenuesDb] = useState([])
  const [venueModalOpen, setVenueModalOpen] = useState(false)
  const [psgGatewayVenue, setPsgGatewayVenue] = useState(null)
  const [venueForm, setVenueForm] = useState({ venue_id: '', name: '', code: '', location: '', active: true, isEdit: false })
  const [savingVenue, setSavingVenue] = useState(false)
  const [deletingVenueId, setDeletingVenueId] = useState(null)
  const reloadVenuesDb = async () => {
    try {
      const r = await authFetch(getLocalApiUrl('/api/venues'))
      const j = await r.json()
      if (j.success) setVenuesDb(j.data)
    } catch {}
  }
  useEffect(() => { reloadVenuesDb() }, [])

  const handleDeleteVenue = async (venue) => {
    const vid = venue.venue_id
    if (!vid) return
    if (!confirm(`Delete venue "${venue.name || vid}" (${vid})? This cannot be undone.`)) return
    try {
      setDeletingVenueId(vid)
      const r = await authFetch(getLocalApiUrl(`/api/venues/${encodeURIComponent(vid)}`), { method: 'DELETE' })
      const j = await r.json()
      if (!j.success) {
        window.alert(j.error || 'Failed to delete venue')
        return
      }
      setVenuesDb((prev) => prev.filter((x) => x.venue_id !== vid))
      if (selectedTradingVenueId === vid) {
        const remaining = venuesDb.filter((x) => x.venue_id !== vid)
        setSelectedTradingVenueId(remaining[0]?.venue_id || '')
      }
    } catch {
      window.alert('Failed to delete venue')
    } finally {
      setDeletingVenueId(null)
    }
  }

  useEffect(() => {
    if (venuesDb && venuesDb.length && !selectedTradingVenueId) {
      setSelectedTradingVenueId(venuesDb[0].venue_id)
    }
  }, [venuesDb, selectedTradingVenueId])

  const loadTradingForVenue = async (venueId) => {
    if (!venueId) return
    try {
      const r = await authFetch(getLocalApiUrl(`/api/venues/${encodeURIComponent(venueId)}/trading-days`))
      const j = await r.json()
      if (j.success) {
        const { weekly = {}, publicHolidaySchedule = {}, holidays = [], events = [] } = j.data || {}
        setVenueTradingDays(prev => ({ ...prev, [venueId]: weekly }))
        setVenueHolidaySchedule(prev => ({ ...prev, [venueId]: publicHolidaySchedule }))
        setHolidaysByVenue(prev => ({ ...prev, [venueId]: holidays }))
        setEventsByVenue(prev => ({ ...prev, [venueId]: events }))
      }
    } catch (err) {
      console.warn('Failed to load trading for venue', venueId, err)
    }
  }

  // Load trading days for ALL venues when venues list is loaded (batch load)
  useEffect(() => {
    if (!venuesDb?.length) return
    let cancelled = false
    setLoadingTrading(true)
    Promise.all(
      venuesDb.map(v => {
        const venueId = v.venue_id ?? v.id
        return venueId ? loadTradingForVenue(venueId) : Promise.resolve()
      })
    ).finally(() => {
      if (!cancelled) setLoadingTrading(false)
    })
    return () => { cancelled = true }
  }, [venuesDb?.length])

  useEffect(() => { if (selectedTradingVenueId) { loadTradingForVenue(selectedTradingVenueId) } }, [selectedTradingVenueId])
  const [tempGroupData, setTempGroupData] = useState({})

  // Import Mapping (Meter, Sessions, EGM, Patron) - declare before loadImportMappings/useEffect
  const [importMappingType, setImportMappingType] = useState('meter')
  const [importMappings, setImportMappings] = useState({ meter: null, sessions: null, egm: null, patron: null, slot_analysis: null, visitor: null })
  const [importTemplates, setImportTemplates] = useState([])
  const [importDefaultTemplateIds, setImportDefaultTemplateIds] = useState({ meter: '', sessions: '', egm: '', patron: '', slot_analysis: '', visitor: '' })
  const [importPreviewTemplateIds, setImportPreviewTemplateIds] = useState({ meter: '', sessions: '', egm: '', patron: '', slot_analysis: '', visitor: '' })
  const [importMappingLoading, setImportMappingLoading] = useState(false)
  const [savingImportMapping, setSavingImportMapping] = useState(false)
  const [savingDefaultTemplate, setSavingDefaultTemplate] = useState(false)
  const [saveTemplateName, setSaveTemplateName] = useState('')
  const [saveTemplateGamingMachineVendor, setSaveTemplateGamingMachineVendor] = useState('RXConnect')
  const [saveTemplateType, setSaveTemplateType] = useState('metering')
  const [mappingPreviewOpen, setMappingPreviewOpen] = useState(false)
  const [templateModalOpen, setTemplateModalOpen] = useState(false)
  const [templateModalMode, setTemplateModalMode] = useState('new') // 'new' | 'edit'
  const [modalDraft, setModalDraft] = useState(emptyPipeMapping())
  const [editingTemplateId, setEditingTemplateId] = useState(null)
  const importMappingJsonInputRef = useRef(null)

  const selectedImportTemplate = useMemo(() => {
    const id = importDefaultTemplateIds[importMappingType]
    if (!id) return null
    return importTemplates.find((x) => String(x.id) === String(id)) || null
  }, [importDefaultTemplateIds, importMappingType, importTemplates])

  const selectedPreviewTemplate = useMemo(() => {
    const id = importPreviewTemplateIds[importMappingType]
    if (!id) return null
    return importTemplates.find((x) => String(x.id) === String(id)) || null
  }, [importPreviewTemplateIds, importMappingType, importTemplates])

  const previewMatchesDefault = Boolean(
    importPreviewTemplateIds[importMappingType] &&
    String(importPreviewTemplateIds[importMappingType]) === String(importDefaultTemplateIds[importMappingType] || '')
  )

  const importConfigKey =
    importMappingType === 'meter' ? 'IMPORT_MAPPING_METER' :
    importMappingType === 'sessions' ? 'IMPORT_MAPPING_SESSIONS' :
    importMappingType === 'patron' ? 'IMPORT_MAPPING_PATRON' :
    importMappingType === 'slot_analysis' ? 'IMPORT_MAPPING_SLOT_ANALYSIS' :
    importMappingType === 'visitor' ? 'IMPORT_MAPPING_VISITOR' :
    'IMPORT_MAPPING_EGM'

  const openNewTemplateModal = (seed = null, suggestedName = '') => {
    setTemplateModalMode('new')
    setEditingTemplateId(null)
    setModalDraft(ensurePipeMapping(seed) || emptyPipeMapping())
    setSaveTemplateName(suggestedName)
    setSaveTemplateType(defaultSaveTemplateTypeForMapping(importMappingType))
    setSaveTemplateGamingMachineVendor('RXConnect')
    setTemplateModalOpen(true)
  }

  const openEditTemplateModal = () => {
    const t = selectedPreviewTemplate
    const cfg = importMappings[importMappingType]
    if (!cfg?.mappings || Object.keys(cfg.mappings).length === 0) {
      toast.error('Select a template first')
      return
    }
    setTemplateModalMode('edit')
    setEditingTemplateId(t?.id || importPreviewTemplateIds[importMappingType] || null)
    setModalDraft(ensurePipeMapping(cfg))
    setSaveTemplateName(t?.template_name || '')
    setSaveTemplateType(t?.type || defaultSaveTemplateTypeForMapping(importMappingType))
    setSaveTemplateGamingMachineVendor(t?.gaming_machine_vendor || 'RXConnect')
    setTemplateModalOpen(true)
  }

  const triggerImportMappingJsonUpload = () => {
    importMappingJsonInputRef.current?.click()
  }

  const downloadCurrentImportMappingJson = () => {
    const cfg = importMappings[importMappingType]
    if (!cfg?.mappings || Object.keys(cfg.mappings).length === 0) {
      toast.error('No mapping for the current template — select or create a template first.')
      return
    }
    const filename = importMappingJsonFilename(importMappingType, selectedPreviewTemplate?.template_name)
    downloadImportMappingJson(filename, cfg)
    toast.success(`Downloaded ${filename}`)
  }

  const handleImportMappingJsonFileSelected = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    try {
      const text = await file.text()
      const loaded = parseUploadedImportMappingJson(text)
      const suggestedName = suggestedTemplateNameFromUploadFile(file.name, importMappingType)
      openNewTemplateModal(loaded, suggestedName)
      toast.success(`Loaded ${file.name} into New template`)
    } catch (err) {
      toast.error(err.message || 'Invalid mapping JSON file')
    }
  }

  const previewImportTemplate = (id) => {
    if (!id) {
      setImportPreviewTemplateIds((prev) => ({ ...prev, [importMappingType]: '' }))
      return
    }
    const t = importTemplates.find((x) => String(x.id) === String(id))
    if (!t?.template_content) return
    const mappingType = t.mapping_type || importMappingType
    setImportMappings((prev) => ({
      ...prev,
      [mappingType]: importMappingFromTemplateContent(t.template_content),
    }))
    setImportPreviewTemplateIds((prev) => ({ ...prev, [mappingType]: id }))
    if (mappingType !== importMappingType) setImportMappingType(mappingType)
  }

  const saveDefaultImportTemplate = async () => {
    const id = importPreviewTemplateIds[importMappingType]
    if (!id) {
      toast.error('Select a template first')
      return
    }
    setSavingDefaultTemplate(true)
    try {
      await saveTenantDefaultImportTemplateId(importMappingType, id)
      setImportDefaultTemplateIds((prev) => ({ ...prev, [importMappingType]: String(id) }))
      toast.success('Default template saved for this tenant')
    } catch (e) {
      toast.error(e?.message || 'Failed to save default template')
    } finally {
      setSavingDefaultTemplate(false)
    }
  }

  const saveTemplateFromModal = async () => {
    const name = saveTemplateName.trim()
    if (!name) {
      toast.error('Template name is required')
      return
    }
    const templateContent = normalizeImportMappingForSave(modalDraft)
    if (!templateContent || !Object.keys(templateContent.mappings || {}).length) {
      toast.error('Add at least one column mapping')
      return
    }
    setSavingImportMapping(true)
    try {
      if (templateModalMode === 'edit' && editingTemplateId) {
        const tr = await authFetch(
          getLocalApiUrl(`/api/import-mapping-templates/${encodeURIComponent(String(editingTemplateId))}`),
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              template_name: name,
              gaming_machine_vendor: saveTemplateGamingMachineVendor,
              type: saveTemplateType,
              mapping_type: importMappingType,
              template_content: templateContent,
            }),
          }
        )
        const tj = await tr.json().catch(() => ({}))
        if (!tr.ok || !tj.success) throw new Error(tj.error || 'Failed to update template')

        await authFetch(getLocalApiUrl(`/api/configuration/${importConfigKey}`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ value: JSON.stringify(templateContent) }),
        })
        setImportMappings((prev) => ({
          ...prev,
          [importMappingType]: importMappingFromTemplateContent(templateContent),
        }))
        toast.success('Template updated')
      } else {
        const r = await authFetch(getLocalApiUrl('/api/import-mapping-templates'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            template_name: name,
            gaming_machine_vendor: saveTemplateGamingMachineVendor,
            type: saveTemplateType,
            mapping_type: importMappingType,
            template_content: templateContent,
          }),
        })
        const j = await r.json()
        if (!j.success) throw new Error(j.error || 'Save failed')
        const newId = j.data?.id
        if (newId) {
          await saveTenantDefaultImportTemplateId(importMappingType, newId)
          await authFetch(getLocalApiUrl(`/api/configuration/${importConfigKey}`), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ value: JSON.stringify(templateContent) }),
          })
          setImportDefaultTemplateIds((prev) => ({ ...prev, [importMappingType]: String(newId) }))
          setImportPreviewTemplateIds((prev) => ({ ...prev, [importMappingType]: newId }))
          setImportMappings((prev) => ({
            ...prev,
            [importMappingType]: importMappingFromTemplateContent(templateContent),
          }))
        }
        toast.success('Template saved and set as current')
      }
      const tmplRes = await authFetch(getLocalApiUrl('/api/import-mapping-templates'))
      const tjList = await tmplRes.json().catch(() => ({}))
      if (tjList.success && Array.isArray(tjList.data)) setImportTemplates(tjList.data)
      setTemplateModalOpen(false)
    } catch (e) {
      console.error('Save template failed', e)
      toast.error(e?.message || 'Save failed')
    } finally {
      setSavingImportMapping(false)
    }
  }

  const loadImportMappings = async () => {
    setImportMappingLoading(true)
    try {
      const [cfgRes, tmplRes, importCfgRes] = await Promise.all([
        authFetch(getLocalApiUrl('/api/configuration')),
        authFetch(getLocalApiUrl('/api/import-mapping-templates')),
        authFetch(getLocalApiUrl('/api/admin/import/config')),
      ])
      const j = await cfgRes.json()
      const tj = await tmplRes.json()
      const importCfgJson = await importCfgRes.json().catch(() => ({}))
      if (tj.success && Array.isArray(tj.data)) setImportTemplates(tj.data)
      if (!j.success || !j.data) return
      const importCat = j.data.find(c => c.category === 'Import')
      const byName = Object.fromEntries((importCat?.settings || []).map(s => [s.name, s]))
      const parse = (key) => {
        const s = byName[key]
        if (!s?.value) return null
        try { return JSON.parse(s.value) } catch { return null }
      }
      setImportMappings({
        meter: importMappingFromTemplateContent(parse('IMPORT_MAPPING_METER')),
        sessions: importMappingFromTemplateContent(parse('IMPORT_MAPPING_SESSIONS')),
        egm: importMappingFromTemplateContent(parse('IMPORT_MAPPING_EGM')),
        patron: importMappingFromTemplateContent(parse('IMPORT_MAPPING_PATRON')),
        slot_analysis: importMappingFromTemplateContent(parse('IMPORT_MAPPING_SLOT_ANALYSIS')),
        visitor: importMappingFromTemplateContent(parse('IMPORT_MAPPING_VISITOR')),
      })
      const tenantDefaults = importCfgJson.success
        ? tenantDefaultTemplateIdsFromImportConfig(importCfgJson.data)
        : { meter: '', sessions: '', egm: '', patron: '', slot_analysis: '', visitor: '' }
      setImportDefaultTemplateIds(tenantDefaults)
      setImportPreviewTemplateIds(tenantDefaults)
    } catch (e) { setImportTemplates([]) }
    finally { setImportMappingLoading(false) }
  }
  useEffect(() => { if (selectedTab === 'import-mapping') loadImportMappings() }, [selectedTab])
  // Do NOT auto-overwrite IMPORT_MAPPING_* config with the default template on load —
  // that wiped Save mapping after refresh. Templates apply only when the user picks one.
  // Serial Numbers
  const [serialNumbers, setSerialNumbers] = useState([])
  const [serialNumbersLoading, setSerialNumbersLoading] = useState(false)
  const [serialEditModal, setSerialEditModal] = useState(null)
  const [serialForm, setSerialForm] = useState({ pattern: '', reset_period: 'yearly', current_value: 0 })
  const [savingSerial, setSavingSerial] = useState(false)
  const [serialPreview, setSerialPreview] = useState('')
  const [serialSaveError, setSerialSaveError] = useState('')
  const [serialPreviewVenueCode, setSerialPreviewVenueCode] = useState('')
  const [serialMigrateLoading, setSerialMigrateLoading] = useState(false)
  const [serialMigrateMessage, setSerialMigrateMessage] = useState('')
  const [serialMigrateError, setSerialMigrateError] = useState('')

  const loadSerialNumbers = async () => {
    setSerialNumbersLoading(true)
    try {
      const r = await authFetch(getLocalApiUrl('/api/admin/serial-numbers'))
      const j = await r.json()
      if (j.success !== false && Array.isArray(j.data || j)) {
        const list = j.data || j
        setSerialNumbers(list)
        if (list.length > 0) {
          setSerialMigrateError('')
          setSerialMigrateMessage('')
        }
      }
    } catch (e) {
      console.warn('Failed to load serial numbers', e)
    } finally {
      setSerialNumbersLoading(false)
    }
  }

  useEffect(() => {
    if (selectedTab === 'serial-numbers') loadSerialNumbers()
  }, [selectedTab])

  useEffect(() => {
    if (!serialEditModal) return
    setSerialPreview(
      previewSerialPattern(serialForm.pattern, serialForm.current_value, serialPreviewVenueCode)
    )
  }, [serialEditModal, serialForm.pattern, serialForm.current_value, serialForm.reset_period, serialPreviewVenueCode])

  const handleSerialEdit = (config) => {
    setSerialSaveError('')
    setSerialPreviewVenueCode('')
    setSerialForm({
      pattern: config.pattern || '',
      reset_period: config.reset_period || 'yearly',
      current_value: config.current_value ?? 0
    })
    setSerialEditModal(config)
  }

  const handleSerialFormChange = (field, value) => {
    setSerialForm((prev) => ({ ...prev, [field]: value }))
  }

  const handleSerialSave = async () => {
    if (!serialEditModal) return
    setSerialSaveError('')
    setSavingSerial(true)
    try {
      const entity = serialEditModal.entity_type
      const r = await authFetch(getLocalApiUrl(`/api/admin/serial-numbers/${encodeURIComponent(entity)}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pattern: serialForm.pattern,
          reset_period: serialForm.reset_period,
          current_value: Number(serialForm.current_value) || 0
        })
      })
      let j = {}
      try {
        j = await r.json()
      } catch {
        j = {}
      }
      if (!r.ok) {
        setSerialSaveError(j.error || `Save failed (${r.status})`)
        return
      }
      setSerialEditModal(null)
      await loadSerialNumbers()
    } catch (e) {
      setSerialSaveError(e.message || 'Failed to save serial number config')
      console.error('Failed to save serial number config', e)
    } finally {
      setSavingSerial(false)
    }
  }

  const handleApplySerialTenantMigrations = async () => {
    setSerialMigrateLoading(true)
    setSerialMigrateError('')
    setSerialMigrateMessage('')
    try {
      const r = await authFetch(getLocalApiUrl('/api/admin/serial-numbers/apply-pending-migrations'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      })
      let j = {}
      try {
        j = await r.json()
      } catch {
        j = {}
      }
      if (!r.ok) {
        setSerialMigrateError(j.error || `Request failed (${r.status})`)
        return
      }
      setSerialMigrateMessage(j.message || 'Migrations completed.')
      await loadSerialNumbers()
    } catch (e) {
      setSerialMigrateError(e.message || 'Migration request failed')
    } finally {
      setSerialMigrateLoading(false)
    }
  }

  // EGMs (machines)
  const [egms, setEgms] = useState([])
  const [egmModalOpen, setEgmModalOpen] = useState(false)
  const [egmForm, setEgmForm] = useState({
    egm_id: '', venue_id: '', house_number: '', serial_number: '', game_name: '', denom: '',
    zone: '', area: '', status: '', machine_class: '', active: true, cameras: [], isEdit: false,
  })
  const [venueCameras, setVenueCameras] = useState([])
  const [savingEgm, setSavingEgm] = useState(false)
  const [masterUploading, setMasterUploading] = useState(false)
  const [masterUploadResult, setMasterUploadResult] = useState(null)
  const [masterVenueId, setMasterVenueId] = useState('')
  const [egmSearch, setEgmSearch] = useState('')
  const [egmActiveFilter, setEgmActiveFilter] = useState('all')
  const [egmTypeFilter, setEgmTypeFilter] = useState('all')
  const [typeBackfillBusy, setTypeBackfillBusy] = useState(false)
  const [clearEgmsBusy, setClearEgmsBusy] = useState(false)

  const egmTypeCounts = useMemo(() => {
    let egm = 0
    let mtgm = 0
    let unset = 0
    for (const m of egms) {
      const cls = String(m.machine_class || '').toUpperCase()
      if (cls === 'EGM') egm += 1
      else if (cls === 'MTGM') mtgm += 1
      else unset += 1
    }
    return { egm, mtgm, unset, total: egms.length }
  }, [egms])

  const filteredEgms = useMemo(() => {
    const q = egmSearch.trim().toLowerCase()
    return egms.filter((m) => {
      if (egmActiveFilter === 'active' && !m.active) return false
      if (egmActiveFilter === 'inactive' && m.active) return false
      const cls = String(m.machine_class || '').toUpperCase()
      if (egmTypeFilter === 'EGM' && cls !== 'EGM') return false
      if (egmTypeFilter === 'MTGM' && cls !== 'MTGM') return false
      if (!q) return true
      const hay = [
        m.house_number,
        m.egm_id,
        m.serial_number,
        m.game_name,
        m.manufacturer,
        m.zone,
        m.area,
        m.status,
        m.machine_class,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return hay.includes(q)
    })
  }, [egms, egmSearch, egmActiveFilter, egmTypeFilter])

  const reloadEgms = async () => {
    try {
      const r = await authFetch(getLocalApiUrl('/api/egms'))
      const j = await r.json()
      if (j.success) setEgms(j.data || [])
    } catch { /* ignore */ }
  }

  const clearAllEgms = async () => {
    const count = egms.length
    if (count === 0) {
      toast.error('No EGMs to clear')
      return
    }
    const ok = window.confirm(
      `Delete all ${count} machine(s) from the EGM registry?\n\nCamera links and denominations synced from these machines will also be removed. Re-import from the master list or DimMachine to restore.`
    )
    if (!ok) return
    try {
      setClearEgmsBusy(true)
      const r = await authFetch(getLocalApiUrl('/api/egms/registry'), { method: 'DELETE' })
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.success) throw new Error(j.error || `Clear failed (${r.status})`)
      const deleted = j.data?.deleted ?? count
      toast.success(`Cleared ${deleted} machine(s) from the registry`)
      setEgmSearch('')
      setEgmActiveFilter('all')
      setEgmTypeFilter('all')
      await reloadEgms()
    } catch (e) {
      toast.error(e.message || 'Failed to clear EGM registry')
    } finally {
      setClearEgmsBusy(false)
    }
  }

  const loadVenueCameras = async (venueId) => {
    if (!venueId) { setVenueCameras([]); return }
    try {
      const r = await authFetch(getLocalApiUrl(`/api/egms/cameras?venue_id=${encodeURIComponent(venueId)}`))
      const j = await r.json()
      if (j.success) setVenueCameras(j.data || [])
    } catch { setVenueCameras([]) }
  }

  const loadEgmCameras = async (egmId) => {
    try {
      const r = await authFetch(getLocalApiUrl(`/api/egms/${encodeURIComponent(egmId)}/cameras`))
      const j = await r.json()
      if (j.success) {
        return (j.data || []).map((c) => ({
          camera_id: c.camera_id,
          priority: c.priority ?? 1,
          notes: c.notes || '',
          name: c.name || '',
        }))
      }
    } catch { /* ignore */ }
    return []
  }

  const uploadMasterList = async (file) => {
    if (!file) return
    setMasterUploading(true)
    setMasterUploadResult(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      if (masterVenueId) fd.append('venue_id', masterVenueId)
      const r = await authFetch(getLocalApiUrl('/api/egms/master/upload'), { method: 'POST', body: fd })
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Upload failed')
      setMasterUploadResult(j)
      await reloadEgms()
    } catch (e) {
      setMasterUploadResult({ success: false, error: e.message || 'Upload failed' })
    } finally {
      setMasterUploading(false)
    }
  }

  useEffect(() => { reloadEgms() }, [])

  // Save/Cancel handlers for Group Info
  const handleSaveGroup = () => {
    setGroupData(prev => ({
      ...prev,
      groupInfo: { ...prev.groupInfo, ...tempGroupData }
    }))
    setEditingGroup(false)
    setTempGroupData({})
  }
  
  const handleCancelGroup = () => {
    setEditingGroup(false)
    setTempGroupData({})
  }
  
  const handleGroupFieldChange = (field, value) => {
    setTempGroupData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  // Venue editing handlers
  const handleEditVenue = (venue) => {
    setEditingVenue(venue.id)
    setTempGroupData(venue)
  }

  const handleSaveVenue = () => {
    setGroupData(prev => ({
      ...prev,
      venues: prev.venues.map(venue => 
        venue.id === editingVenue 
          ? { ...venue, ...tempGroupData }
          : venue
      )
    }))
    setEditingVenue(null)
    setTempGroupData({})
  }

  const handleCancelVenue = () => {
    setEditingVenue(null)
    setTempGroupData({})
  }

  const handleVenueFieldChange = (field, value) => {
    setTempGroupData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const dayOrder = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday']
  const dayLabel = {
    monday:'Mon', tuesday:'Tue', wednesday:'Wed', thursday:'Thu', friday:'Fri', saturday:'Sat', sunday:'Sun'
  }
  const defaultClosedDay = { start: '09:00', end: '17:00', enabled: false, nextDay: false }
  const buildTempVenueTrading = (venueId) => {
    const saved = venueTradingDays[venueId] || {}
    return Object.fromEntries(
      dayOrder.map(day => [day, saved[day] ? { ...saved[day] } : { ...defaultClosedDay }])
    )
  }
  const postTradingDays = async (venueId, payload) => {
    const r = await authFetch(
      getLocalApiUrl(`/api/venues/${encodeURIComponent(venueId)}/trading-days`),
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }
    )
    const j = await r.json().catch(() => ({}))
    if (!r.ok || !j.success) {
      throw new Error(j.error || `Save failed (${r.status})`)
    }
    return j
  }
  const formatCell = (sch, isLoading) => {
    if (isLoading && sch === undefined) return '…'
    if (!sch || !sch.enabled) return 'Closed'
    const start = (sch.start || '').replace('+1','')
    const endRaw = sch.end || ''
    const end = endRaw.replace('+1','')
    const plus = endRaw.includes('+1') ? ' +1' : ''
    return `${start}-${end}${plus}`
  }

  const getNextVenueId = () => {
    const ids = venuesDb.map(v => parseInt(String(v.venue_id || v.id).replace(/\D/g, '')) || 0)
    const next = Math.max(0, ...ids) + 1
    return 'V' + String(next).padStart(3, '0')
  }

  const handleNewVenueFieldChange = (field, value) => {
    setNewVenueData(prev => ({ ...prev, [field]: value }))
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <HomeIcon className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Group Configuration</h1>
                <p className="text-gray-600">
                  Centralized configuration for{' '}
                  {groupProfileLoading ? '…' : (groupData.groupInfo.groupName || 'this organization')}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <StatusBadge status={groupData.groupInfo.status || 'Unknown'} />
              <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                <CogIcon className="w-4 h-4 mr-2" />
                System Settings
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'overview', name: 'Group Overview', icon: BuildingOfficeIcon },
              { id: 'trading', name: 'Trading Days', icon: CalendarDaysIcon },
              { id: 'venues', name: 'Venues', icon: MapPinIcon },
              { id: 'egms', name: 'EGMs', icon: CogIcon },
              { id: 'import-mapping', name: 'Import Mapping', icon: ArrowUpTrayIcon },
              { id: 'serial-numbers', name: 'Serial Numbers', icon: HashtagIcon },
              { id: 'settings', name: 'Group Settings', icon: CogIcon }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setSelectedTab(tab.id)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  selectedTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Overview Tab */}
        {selectedTab === 'overview' && (
          <div className="space-y-6">
            <InfoCard
              title="Group Information"
              icon={BuildingOfficeIcon}
              isEditing={editingGroup}
              onSave={handleSaveGroup}
              onCancel={handleCancelGroup}
              action={
                <button
                  onClick={() => {
                    setEditingGroup(true)
                    setTempGroupData(groupData.groupInfo)
                  }}
                  className="flex items-center px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <PencilIcon className="w-3 h-3 mr-1" />
                  Edit
                </button>
              }
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Group Name</label>
                  {editingGroup ? (
                    <input
                      type="text"
                      value={tempGroupData.groupName || groupData.groupInfo.groupName}
                      onChange={(e) => handleGroupFieldChange('groupName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                  <p className="text-sm text-gray-900 font-bold">{groupData.groupInfo.groupName}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Group System ID (GSID)</label>
                  {editingGroup ? (
                    <input
                      type="text"
                      value={tempGroupData.gsid || groupData.groupInfo.gsid}
                      onChange={(e) => handleGroupFieldChange('gsid', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                    />
                  ) : (
                    <p className="text-sm text-gray-900 font-mono">{groupData.groupInfo.gsid}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Group Code</label>
                  {editingGroup ? (
                    <input
                      type="text"
                      value={tempGroupData.groupCode || groupData.groupInfo.groupCode}
                      onChange={(e) => handleGroupFieldChange('groupCode', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                    />
                  ) : (
                    <p className="text-sm text-gray-900 font-mono">{groupData.groupInfo.groupCode}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Primary Timezone</label>
                  {editingGroup ? (
                    <SelectDropdown
                      value={tempGroupData.timezone || groupData.groupInfo.timezone}
                      onChange={(e) => handleGroupFieldChange('timezone', e.target.value)}
                      className="w-full"
                      options={[
                        { value: 'Australia/Sydney', label: 'Australia/Sydney (AEDT/AEST)' },
                        { value: 'Australia/Melbourne', label: 'Australia/Melbourne (AEDT/AEST)' },
                        { value: 'Australia/Brisbane', label: 'Australia/Brisbane (AEST)' },
                        { value: 'Australia/Perth', label: 'Australia/Perth (AWST)' },
                        { value: 'Australia/Adelaide', label: 'Australia/Adelaide (ACDT/ACST)' },
                        { value: 'Australia/Darwin', label: 'Australia/Darwin (ACST)' },
                        { value: 'Australia/Hobart', label: 'Australia/Hobart (AEDT/AEST)' },
                      ]}
                    />
                  ) : (
                    <p className="text-sm text-gray-900">{groupData.groupInfo.timezone}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                  {editingGroup ? (
                    <input
                      type="text"
                      value={tempGroupData.timezoneAbbrev || groupData.groupInfo.timezoneAbbrev}
                      onChange={(e) => handleGroupFieldChange('timezoneAbbrev', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-sm text-gray-900">{groupData.groupInfo.timezoneAbbrev}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Default Currency</label>
                  {editingGroup ? (
                    <SelectDropdown
                      value={tempGroupData.currency || groupData.groupInfo.currency}
                      onChange={(e) => handleGroupFieldChange('currency', e.target.value)}
                      className="w-full"
                      options={[
                        { value: 'AUD', label: 'AUD (Australian Dollar)' },
                        { value: 'USD', label: 'USD (US Dollar)' },
                        { value: 'EUR', label: 'EUR (Euro)' },
                        { value: 'GBP', label: 'GBP (British Pound)' },
                        { value: 'CAD', label: 'CAD (Canadian Dollar)' },
                        { value: 'NZD', label: 'NZD (New Zealand Dollar)' },
                      ]}
                    />
                  ) : (
                    <p className="text-sm text-gray-900">{groupData.groupInfo.currency}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Regulatory Jurisdiction</label>
                  {editingGroup ? (
                    <input
                      type="text"
                      value={tempGroupData.jurisdiction || groupData.groupInfo.jurisdiction}
                      onChange={(e) => handleGroupFieldChange('jurisdiction', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-sm text-gray-900">{groupData.groupInfo.jurisdiction}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Established</label>
                  {editingGroup ? (
                    <input
                      type="date"
                      value={tempGroupData.established || groupData.groupInfo.established}
                      onChange={(e) => handleGroupFieldChange('established', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-sm text-gray-900">
                      {groupData.groupInfo.established && !Number.isNaN(Date.parse(groupData.groupInfo.established))
                        ? new Date(groupData.groupInfo.established).toLocaleDateString()
                        : '—'}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Headquarters</label>
                  {editingGroup ? (
                    <input
                      type="text"
                      value={tempGroupData.headquarters || groupData.groupInfo.headquarters}
                      onChange={(e) => handleGroupFieldChange('headquarters', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-sm text-gray-900">{groupData.groupInfo.headquarters}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  {editingGroup ? (
                    <SelectDropdown
                      value={tempGroupData.status || groupData.groupInfo.status}
                      onChange={(e) => handleGroupFieldChange('status', e.target.value)}
                      className="w-full"
                      options={[
                        { value: 'Active', label: 'Active' },
                        { value: 'Inactive', label: 'Inactive' },
                        { value: 'Maintenance', label: 'Maintenance' },
                      ]}
                    />
                  ) : (
                    <StatusBadge status={groupData.groupInfo.status || 'Unknown'} />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Membership System</label>
                  {editingGroup ? (
                    <input
                      type="text"
                      value={tempGroupData.membershipSystem || groupData.groupInfo.membershipSystem}
                      onChange={(e) => handleGroupFieldChange('membershipSystem', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-sm text-gray-900">{groupData.groupInfo.membershipSystem}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Visitor Management System</label>
                  {editingGroup ? (
                    <input
                      type="text"
                      value={tempGroupData.visitorManagementSystem || groupData.groupInfo.visitorManagementSystem}
                      onChange={(e) => handleGroupFieldChange('visitorManagementSystem', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-sm text-gray-900">{groupData.groupInfo.visitorManagementSystem}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gaming System</label>
                  {editingGroup ? (
                    <input
                      type="text"
                      value={tempGroupData.gamingSystem || groupData.groupInfo.gamingSystem}
                      onChange={(e) => handleGroupFieldChange('gamingSystem', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-sm text-gray-900">{groupData.groupInfo.gamingSystem}</p>
                  )}
                </div>
              </div>
            </InfoCard>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Venues</p>
                    <p className="text-2xl font-bold text-blue-600">{venuesDb.length}</p>
                    <p className="text-sm text-gray-500">{venuesDb.filter(v => v.status === 'Active').length} Active</p>
                  </div>
                  <MapPinIcon className="w-8 h-8 text-blue-100" />
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total EGMs</p>
                    <p className="text-2xl font-bold text-green-600">
                      {(groupData.groupInfo.totalEGMs ?? venuesDb.reduce((sum, venue) => sum + venue.gameCount, 0)).toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-500">Across all venues</p>
                  </div>
                  <CogIcon className="w-8 h-8 text-green-100" />
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total MTGM</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {(groupData.groupInfo.totalMTGMs ?? venuesDb.reduce((sum, venue) => sum + venue.tableCount, 0)).toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-500">Across all venues</p>
                  </div>
                  <HomeIcon className="w-8 h-8 text-purple-100" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Trading Days Tab */}
        {selectedTab === 'trading' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Venue:</span>
                <SelectDropdown
                  className="min-w-[10rem] max-w-xs"
                  value={selectedTradingVenueId}
                  onChange={(e) => setSelectedTradingVenueId(e.target.value)}
                  options={(venuesDb || []).map((v) => ({ value: v.venue_id, label: v.venue_id }))}
                  placeholder="Venue"
                />
                {loadingTrading && <span className="text-xs text-gray-500">Loading…</span>}
              </div>
            </div>
            {tradingSaveError && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{tradingSaveError}</p>
            )}

            <InfoCard 
              title="Public Holiday Schedule" 
              icon={ClockIcon}
              isEditing={editingTrading}
              onSave={() => {
                (async () => {
                  setTradingSaveError('')
                  try {
                    await postTradingDays(selectedTradingVenueId, {
                      publicHolidaySchedule: tempGroupData.publicHolidaySchedule,
                    })
                    await loadTradingForVenue(selectedTradingVenueId)
                    setEditingTrading(false)
                    setTempGroupData({})
                  } catch (e) {
                    setTradingSaveError(e.message || 'Failed to save public holiday schedule')
                  }
                })()
              }}
              onCancel={() => {
                setEditingTrading(false)
                setTempGroupData({})
              }}
              action={
                <button
                  onClick={() => {
                    setEditingTrading(true)
                    setTempGroupData({ publicHolidaySchedule: venueHolidaySchedule[selectedTradingVenueId] || { enabled:false, start:'00:00', end:'00:00', notes:'' } })
                  }}
                  className="flex items-center px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <PencilIcon className="w-3 h-3 mr-1" />
                  Edit Holiday Schedule
                </button>
              }
            >
              <div className="p-4 bg-orange-50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">Default Public Holiday Hours</h4>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={editingTrading ? (tempGroupData.publicHolidaySchedule?.enabled ?? (venueHolidaySchedule[selectedTradingVenueId]?.enabled || false)) : (venueHolidaySchedule[selectedTradingVenueId]?.enabled || false)}
                      onChange={(e) => {
                        if (editingTrading) {
                          setTempGroupData(prev => ({
                            ...prev,
                            publicHolidaySchedule: { ...prev.publicHolidaySchedule || (venueHolidaySchedule[selectedTradingVenueId] || {}), enabled: e.target.checked }
                          }))
                        }
                      }}
                      disabled={!editingTrading}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-600">Holiday Operations Enabled</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">Start Time:</span>
                    {editingTrading ? (
                      <input
                        type="time"
                        value={tempGroupData.publicHolidaySchedule?.start || venueHolidaySchedule[selectedTradingVenueId]?.start || '00:00'}
                        onChange={(e) => {
                          setTempGroupData(prev => ({
                            ...prev,
                            publicHolidaySchedule: { ...prev.publicHolidaySchedule || (venueHolidaySchedule[selectedTradingVenueId] || {}), start: e.target.value }
                          }))
                        }}
                        className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <span className="text-sm font-medium text-gray-900">{venueHolidaySchedule[selectedTradingVenueId]?.start || '—'}</span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">End Time:</span>
                    {editingTrading ? (
                      <input
                        type="time"
                        value={tempGroupData.publicHolidaySchedule?.end || venueHolidaySchedule[selectedTradingVenueId]?.end || '00:00'}
                        onChange={(e) => {
                          setTempGroupData(prev => ({
                            ...prev,
                            publicHolidaySchedule: { ...prev.publicHolidaySchedule || (venueHolidaySchedule[selectedTradingVenueId] || {}), end: e.target.value }
                          }))
                        }}
                        className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <span className="text-sm font-medium text-gray-900">{venueHolidaySchedule[selectedTradingVenueId]?.end || '—'}</span>
                    )}
                  </div>
                </div>
                <div className="mt-3">
                  <span className="text-sm text-gray-600">Notes:</span>
                  {editingTrading ? (
                    <input
                      type="text"
                      value={tempGroupData.publicHolidaySchedule?.notes || venueHolidaySchedule[selectedTradingVenueId]?.notes || ''}
                      onChange={(e) => {
                        setTempGroupData(prev => ({
                          ...prev,
                          publicHolidaySchedule: { ...prev.publicHolidaySchedule || (venueHolidaySchedule[selectedTradingVenueId] || {}), notes: e.target.value }
                        }))
                      }}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <span className="text-sm text-gray-700 ml-2">{venueHolidaySchedule[selectedTradingVenueId]?.notes || '—'}</span>
                  )}
                </div>
              </div>
            </InfoCard>

            {/* Per-Venue Trading Schedules */}
            <InfoCard title="Venue Trading Schedules" icon={CalendarDaysIcon}
              action={null}
            >
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Venue</th>
                      {dayOrder.map(d => (
                        <th key={d} className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{dayLabel[d]}</th>
                      ))}
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {venuesDb.length === 0 && (
                      <tr><td className="px-4 py-6 text-center text-gray-500" colSpan={9}>No venues. Add venues in the Venues tab.</td></tr>
                    )}
                    {venuesDb.map(v => {
                      const vid = v.venue_id || v.id
                      return (
                      <tr key={vid} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{v.name}</td>
                        {dayOrder.map(d => (
                          <td key={`${vid}-${d}`} className="px-2 py-3 text-center text-xs text-gray-700">
                            {formatCell(venueTradingDays[vid]?.[d], loadingTrading)}
                          </td>
                        ))}
                        <td className="px-4 py-3 text-sm">
                          <button
                            onClick={async () => {
                              setTradingSaveError('')
                              if (venueTradingDays[vid] === undefined) await loadTradingForVenue(vid)
                              setTempVenueTrading(buildTempVenueTrading(vid))
                              setEditingVenueTradingId(vid)
                            }}
                            className="inline-flex items-center px-3 py-1.5 rounded-md bg-blue-100 text-blue-700 hover:bg-blue-200"
                          >
                            <PencilIcon className="h-4 w-4 mr-1" /> Edit
                          </button>
                        </td>
                      </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </InfoCard>

            {editingVenueTradingId && (
              <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-3xl shadow-lg rounded-md bg-white">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Edit Trading Schedule — {venuesDb.find(v => (v.venue_id || v.id) === editingVenueTradingId)?.name}
                    </h3>
                    <button onClick={() => { setEditingVenueTradingId(null); setTempVenueTrading({}); }} className="text-gray-500 hover:text-gray-700">
                      <XCircleIcon className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="space-y-3">
                    {dayOrder.map(day => {
                      const current = tempVenueTrading[day] || buildTempVenueTrading(editingVenueTradingId)[day]
                      return (
                        <div key={day} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                          <div className="w-20 text-sm font-medium text-gray-900 capitalize">{day}</div>
                          <div className="flex items-center gap-3">
                            <label className="flex items-center text-xs text-gray-600">
                              <input
                                type="checkbox"
                                checked={current.enabled}
                                onChange={(e) => setTempVenueTrading(prev => ({ ...prev, [day]: { ...current, enabled: e.target.checked } }))}
                                className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              Enabled
                            </label>
                            <span className="text-xs text-gray-600">Start</span>
                            <input
                              type="time"
                              value={current.start.replace('+1','')}
                              onChange={(e) => setTempVenueTrading(prev => ({ ...prev, [day]: { ...current, start: e.target.value } }))}
                              className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <span className="text-xs text-gray-600">End</span>
                            <input
                              type="time"
                              value={current.end.replace('+1','')}
                              onChange={(e) => setTempVenueTrading(prev => ({ ...prev, [day]: { ...current, end: current.nextDay ? e.target.value + '+1' : e.target.value } }))}
                              className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <label className="flex items-center text-xs text-gray-600">
                              <input
                                type="checkbox"
                                checked={current.nextDay}
                                onChange={(e) => {
                                  const endBase = current.end.replace('+1','')
                                  setTempVenueTrading(prev => ({ ...prev, [day]: { ...current, nextDay: e.target.checked, end: e.target.checked ? endBase + '+1' : endBase } }))
                                }}
                                className="mr-2 h-3 w-3"
                              />
                              Next Day
                            </label>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  <div className="flex justify-end gap-2 mt-5">
                    <button onClick={() => { setEditingVenueTradingId(null); setTempVenueTrading({}); }} className="px-3 py-1.5 rounded-md bg-gray-200 text-gray-800 hover:bg-gray-300">Cancel</button>
                    <button
                      disabled={!!savingVenueTradingId}
                      onClick={async () => {
                        const vid = editingVenueTradingId
                        setTradingSaveError('')
                        setSavingVenueTradingId(vid)
                        try {
                          const weekly = { ...buildTempVenueTrading(vid), ...tempVenueTrading }
                          await postTradingDays(vid, { weekly })
                          await loadTradingForVenue(vid)
                          setEditingVenueTradingId(null)
                          setTempVenueTrading({})
                        } catch (e) {
                          setTradingSaveError(e.message || 'Failed to save trading schedule')
                        } finally {
                          setSavingVenueTradingId(null)
                        }
                      }}
                      className="px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      {savingVenueTradingId ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <InfoCard title="Public Holidays Calendar" icon={ClockIcon}>
                <div className="space-y-3">
                  {(holidaysByVenue[selectedTradingVenueId] || []).map((holiday, index) => (
                    <div key={index} className="flex justify-between items-center p-3 border border-gray-200 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{holiday.name}</p>
                        <p className="text-sm text-gray-500">{holiday.type || 'Public Holiday'}</p>
                        {holiday.closed && (
                          <span className="inline-flex px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full mt-1">
                            Closed
                          </span>
                        )}
                        {holiday.useHolidaySchedule && !holiday.closed && (
                          <span className="inline-flex px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded-full mt-1">
                            Holiday Hours
                          </span>
                        )}
                      </div>
                      <span className="text-sm text-gray-700">{new Date(holiday.holiday_date).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              </InfoCard>

              <InfoCard title="Special Events" icon={CalendarDaysIcon}>
                <div className="space-y-3">
                  {(eventsByVenue[selectedTradingVenueId] || []).map((event, index) => (
                    <div key={index} className="flex justify-between items-center p-3 border border-gray-200 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{event.name}</p>
                        <p className="text-sm text-gray-500">{event.type}</p>
                        {event.useHolidaySchedule && (
                          <span className="inline-flex px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded-full mt-1">
                            Holiday Hours
                          </span>
                        )}
                        {event.useNormalSchedule && (
                          <span className="inline-flex px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full mt-1">
                            Normal Hours
                          </span>
                        )}
                      </div>
                      <span className="text-sm text-gray-700">{new Date(event.event_date).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              </InfoCard>
            </div>
          </div>
        )}

        {/* Venues Tab */}
        {selectedTab === 'venues' && (
          <div className="space-y-6">
            {/* DB Venues listing */
            }
            <InfoCard
              title="Venues"
              icon={MapPinIcon}
              action={
                <button onClick={()=>{ setVenueForm({ venue_id:'', name:'', code:'', location:'', active:true, isEdit:false }); setVenueModalOpen(true) }} className="flex items-center px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors">
                  <PlusIcon className="w-3 h-3 mr-1" />
                  Add Venue
                </button>
              }
            >
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Venue ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {venuesDb.map(v => (
                      <tr key={v.venue_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">{v.venue_id}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{v.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{v.location || '—'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{v.code || '—'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{v.active ? <span className="px-2 py-0.5 rounded bg-green-100 text-green-800">Active</span> : <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-800">Inactive</span>}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="inline-flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setPsgGatewayVenue({ venue_id: v.venue_id, name: v.name })}
                              className="inline-flex items-center px-3 py-1.5 rounded-md bg-teal-100 text-teal-800 hover:bg-teal-200"
                              title="PSG Gateway integrations, functions, interface data, devices"
                            >
                              <ServerStackIcon className="h-4 w-4 mr-1" /> PSG Gateway
                            </button>
                            <button
                              onClick={()=>{ setVenueForm({ original_venue_id: v.venue_id, venue_id: v.venue_id, name: v.name || '', code: v.code || '', location: v.location || '', active: !!v.active, isEdit:true }); setVenueModalOpen(true) }}
                              className="inline-flex items-center px-3 py-1.5 rounded-md bg-blue-100 text-blue-700 hover:bg-blue-200"
                            >
                              <PencilIcon className="h-4 w-4 mr-1" /> Edit
                            </button>
                            <button
                              onClick={() => handleDeleteVenue(v)}
                              disabled={deletingVenueId === v.venue_id}
                              className="inline-flex items-center px-3 py-1.5 rounded-md bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50"
                            >
                              <TrashIcon className="h-4 w-4 mr-1" /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {venuesDb.length===0 && (
                      <tr><td className="px-6 py-4 text-gray-500" colSpan={6}>No venues in database</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </InfoCard>

            {psgGatewayVenue && (
              <VenuePsgGatewayModal venue={psgGatewayVenue} onClose={() => setPsgGatewayVenue(null)} />
            )}

            {venueModalOpen && (
              <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                <div className="relative top-24 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Add Venue</h3>
                    <button onClick={()=>setVenueModalOpen(false)} className="text-gray-500 hover:text-gray-700">
                      <XCircleIcon className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Venue ID</label>
                      <input className="input-field" placeholder="diggers / leagues / ..." value={venueForm.venue_id} onChange={(e)=>setVenueForm(prev=>({...prev, venue_id:e.target.value}))} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Venue Name</label>
                      <input className="input-field" value={venueForm.name} onChange={(e)=>setVenueForm(prev=>({...prev, name:e.target.value}))} />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs text-gray-600 mb-1">Location</label>
                      <input className="input-field" placeholder="City, State" value={venueForm.location} onChange={(e)=>setVenueForm(prev=>({...prev, location:e.target.value}))} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Code</label>
                      <input className="input-field" value={venueForm.code} onChange={(e)=>setVenueForm(prev=>({...prev, code:e.target.value}))} />
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" checked={venueForm.active} onChange={(e)=>setVenueForm(prev=>({...prev, active:e.target.checked}))} />
                      <span className="text-sm">Active</span>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs text-gray-600 mb-1">Gaming extract venue name</label>
                      <input className="input-field" placeholder="Name as it appears in gaming data extract files (e.g. FactPlayerSession)" value={venueForm.gaming_extract_venue_name || ''} onChange={(e)=>setVenueForm(prev=>({...prev, gaming_extract_venue_name:e.target.value}))} />
                      <p className="text-xs text-gray-400 mt-0.5">Used to match gaming sessions. Leave blank to use the display name above.</p>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-5">
                    <button onClick={()=>setVenueModalOpen(false)} className="px-3 py-1.5 rounded-md bg-gray-200 text-gray-800 hover:bg-gray-300">Cancel</button>
                    <button
                      disabled={!venueForm.venue_id || !venueForm.name || savingVenue}
                      onClick={async ()=>{
                        try {
                          setSavingVenue(true)
                           const payload = { original_venue_id: venueForm.original_venue_id, venue_id: venueForm.venue_id, name: venueForm.name, code: venueForm.code, location: venueForm.location, active: venueForm.active, gaming_extract_venue_name: venueForm.gaming_extract_venue_name || null }
                          const r = await authFetch(getLocalApiUrl('/api/venues'), { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(payload) })
                          const j = await r.json()
                          if (!j.success) {
                            window.alert(j.error || 'Failed to save venue')
                            return
                          }
                          setVenueModalOpen(false)
                          await reloadVenuesDb()
                        } catch {
                          window.alert('Failed to save venue')
                        } finally { setSavingVenue(false) }
                      }}
                      className="px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                    >Save</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* EGMs Tab */}
        {selectedTab === 'egms' && (
          <div className="space-y-6">
            <InfoCard
              title="EGM Registry"
              icon={CogIcon}
              action={
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => {
                      setEgmForm({
                        egm_id: '', venue_id: '', house_number: '', serial_number: '', game_name: '', denom: '',
                        zone: '', area: '', status: '', machine_class: '', active: true, cameras: [], isEdit: false,
                      })
                      setVenueCameras([])
                      setEgmModalOpen(true)
                    }}
                    className="flex items-center px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                  >
                    <PlusIcon className="w-3 h-3 mr-1" />
                    Add EGM
                  </button>
                  <button
                    type="button"
                    disabled={clearEgmsBusy || egms.length === 0}
                    onClick={clearAllEgms}
                    className="flex items-center px-3 py-1 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <TrashIcon className="w-3 h-3 mr-1" />
                    {clearEgmsBusy ? 'Clearing…' : 'Clear all EGMs'}
                  </button>
                </div>
              }
            >
              <p className="text-sm text-gray-600 mb-4">
                Bootstrap the floor inventory from the client <strong>Machine master list</strong> (house #, serial, denom, game).
                Daily <strong>DimMachine</strong> imports merge status, zone, and vendor IDs onto the same rows — cameras are configured here only.
                DimMachine pipe layout: <button type="button" className="text-blue-700 underline" onClick={() => setSelectedTab('import-mapping')}>Import Mapping → EGM</button>
                {' '}— configure the default template and download the column schema reference.
              </p>

              <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50/60 p-3 text-xs text-gray-700">
                <p className="font-medium text-gray-900 mb-2">Machine master list — file layout</p>
                <p className="mb-2 text-gray-600">
                  Four columns per row: <strong>house #</strong> (5–6 digits), <strong>serial</strong>, <strong>denom</strong> (e.g. 0.01), <strong>game name</strong>.
                  Header row and <code className="bg-white px-1">#</code> comment lines are skipped. Upload <code className="bg-white px-1">.xlsx</code> or <code className="bg-white px-1">.csv</code>.
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-white border border-blue-200 text-blue-900 hover:bg-blue-100"
                    onClick={() => downloadMachineMasterTemplate()}
                  >
                    <ArrowDownTrayIcon className="w-3.5 h-3.5 mr-1" />
                    Download template (CSV for Excel)
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-white border border-blue-200 text-blue-900 hover:bg-blue-100"
                    onClick={() => downloadMachineMasterSample()}
                  >
                    <ArrowDownTrayIcon className="w-3.5 h-3.5 mr-1" />
                    Download sample (3 machines, no header)
                  </button>
                </div>
              </div>

              <div className="mb-4 p-4 rounded-lg border border-dashed border-gray-300 bg-gray-50">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Import machine master list (one-time)</h4>
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Default venue</label>
                    <SelectDropdown
                      className="min-w-[200px]"
                      value={masterVenueId}
                      onChange={(e) => setMasterVenueId(e.target.value)}
                      options={[
                        { value: '', label: '— Optional —' },
                        ...venuesDb.map((v) => ({ value: v.venue_id, label: v.name || v.venue_id })),
                      ]}
                      placeholder="Select venue"
                    />
                  </div>
                  <div>
                    <input
                      type="file"
                      id="egm-master-upload"
                      className="hidden"
                      accept=".xlsx,.xls,.csv"
                      onChange={(e) => {
                        const f = e.target.files?.[0]
                        if (f) uploadMasterList(f)
                        e.target.value = ''
                      }}
                    />
                    <label
                      htmlFor="egm-master-upload"
                      className={`inline-flex items-center px-3 py-2 text-sm rounded-lg cursor-pointer ${
                        masterUploading ? 'bg-gray-200 text-gray-500' : 'bg-psg-teal text-white hover:opacity-90'
                      }`}
                    >
                      <ArrowUpTrayIcon className="w-4 h-4 mr-1" />
                      {masterUploading ? 'Importing…' : 'Upload master list'}
                    </label>
                  </div>
                  {masterUploadResult && (
                    <div className="text-sm">
                      <span className={masterUploadResult.success === false ? 'text-red-600' : 'text-green-700'}>
                        {masterUploadResult.success === false
                          ? (masterUploadResult.error || 'Import failed')
                          : `${masterUploadResult.imported ?? 0} machines · ${masterUploadResult.errors ?? 0} errors`}
                      </span>
                      {masterUploadResult.errors > 0 && masterUploadResult.error_details?.length > 0 && (
                        <details className="mt-2 text-xs text-red-800">
                          <summary className="cursor-pointer font-medium">Show error details</summary>
                          <ul className="mt-1 max-h-32 overflow-y-auto font-mono space-y-0.5">
                            {masterUploadResult.error_details.map((line, i) => (
                              <li key={i}>{line}</li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-4 flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:items-end">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs text-gray-600 mb-1">Search</label>
                  <div className="relative">
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="search"
                      className="input-field pl-9 w-full"
                      placeholder="House #, serial, game name…"
                      value={egmSearch}
                      onChange={(e) => setEgmSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div className="min-w-[160px]">
                  <label className="block text-xs text-gray-600 mb-1">Active</label>
                  <SelectDropdown
                    className="w-full"
                    value={egmActiveFilter}
                    onChange={(e) => setEgmActiveFilter(e.target.value)}
                    options={[
                      { value: 'all', label: 'All' },
                      { value: 'active', label: 'Active only' },
                      { value: 'inactive', label: 'Inactive only' },
                    ]}
                  />
                </div>
                <div className="min-w-[160px]">
                  <label className="block text-xs text-gray-600 mb-1">Type</label>
                  <SelectDropdown
                    className="w-full"
                    value={egmTypeFilter}
                    onChange={(e) => setEgmTypeFilter(e.target.value)}
                    options={[
                      { value: 'all', label: 'All (EGM + MTGM)' },
                      { value: 'EGM', label: `EGM (normal)${egmTypeCounts.egm ? ` · ${egmTypeCounts.egm}` : ''}` },
                      { value: 'MTGM', label: `MTGM (multi)${egmTypeCounts.mtgm ? ` · ${egmTypeCounts.mtgm}` : ''}` },
                    ]}
                  />
                </div>
                {(egmSearch || egmActiveFilter !== 'all' || egmTypeFilter !== 'all') && (
                  <button
                    type="button"
                    className="text-sm text-blue-700 hover:text-blue-900 underline"
                    onClick={() => {
                      setEgmSearch('')
                      setEgmActiveFilter('all')
                      setEgmTypeFilter('all')
                    }}
                  >
                    Clear filters
                  </button>
                )}
              </div>

              {egmTypeCounts.total > 0 && egmTypeCounts.unset === egmTypeCounts.total && (
                <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm text-amber-900">
                    Type (EGM / MTGM) is not set on any machine yet. Populate from DimMachine, then the Type filter will work.
                  </p>
                  <button
                    type="button"
                    className="btn-secondary !py-1.5 !px-3 !text-sm whitespace-nowrap"
                    disabled={typeBackfillBusy}
                    onClick={async () => {
                      try {
                        setTypeBackfillBusy(true)
                        const r = await authFetch(getLocalApiUrl('/api/egms/backfill-machine-class'), {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({}),
                        })
                        const j = await r.json().catch(() => ({}))
                        if (!r.ok || !j.success) throw new Error(j.error || `Backfill failed (${r.status})`)
                        const c = j.counts || {}
                        toast.success(`Types updated — EGM ${c.egm ?? 0}, MTGM ${c.mtgm ?? 0}, unset ${c.unset ?? 0}`)
                        await reloadEgms()
                      } catch (e) {
                        toast.error(e.message || 'Populate types failed — try Daily Data → EGM import')
                      } finally {
                        setTypeBackfillBusy(false)
                      }
                    }}
                  >
                    {typeBackfillBusy ? 'Populating…' : 'Populate types from DimMachine'}
                  </button>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">#</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">House No</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Serial</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Game</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Denom</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zone</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Venue</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cameras</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredEgms.map((m, idx) => (
                      <tr key={m.egm_id} className="hover:bg-gray-50">
                        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500 tabular-nums">{idx + 1}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-mono">{m.house_number || m.egm_id}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {String(m.machine_class || '').toUpperCase() === 'MTGM' ? (
                            <span className="px-2 py-0.5 rounded bg-violet-100 text-violet-800 text-xs font-medium">MTGM</span>
                          ) : String(m.machine_class || '').toUpperCase() === 'EGM' ? (
                            <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-medium">EGM</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-600">{m.serial_number || '—'}</td>
                        <td className="px-4 py-3 text-sm max-w-[180px] truncate" title={m.game_name || ''}>{m.game_name || '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">{m.denom != null ? Number(m.denom) : '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">{m.zone || m.area || '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">{m.status || '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">{m.venue_id || '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {m.active ? (
                            <span className="px-2 py-0.5 rounded bg-green-100 text-green-800">Active</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-800">Inactive</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">{m.camera_count > 0 ? m.camera_count : '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <button
                            onClick={async () => {
                              const cameras = await loadEgmCameras(m.egm_id)
                              setEgmForm({
                                egm_id: m.egm_id,
                                venue_id: m.venue_id || '',
                                house_number: m.house_number || '',
                                serial_number: m.serial_number || '',
                                game_name: m.game_name || '',
                                denom: m.denom != null ? String(m.denom) : '',
                                zone: m.zone || '',
                                area: m.area || '',
                                status: m.status || '',
                                machine_class: m.machine_class || '',
                                active: !!m.active,
                                cameras: cameras.length ? cameras : [{ camera_id: '', priority: 1, notes: '' }],
                                isEdit: true,
                              })
                              await loadVenueCameras(m.venue_id)
                              setEgmModalOpen(true)
                            }}
                            className="inline-flex items-center px-3 py-1.5 rounded-md bg-blue-100 text-blue-700 hover:bg-blue-200"
                          >
                            <PencilIcon className="h-4 w-4 mr-1" /> Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                    {egms.length === 0 && (
                      <tr><td className="px-4 py-6 text-gray-500 text-center" colSpan={12}>No EGMs registered — upload the client machine master list to bootstrap.</td></tr>
                    )}
                    {egms.length > 0 && filteredEgms.length === 0 && (
                      <tr><td className="px-4 py-6 text-gray-500 text-center" colSpan={12}>
                        {egmTypeFilter !== 'all' && egmTypeCounts.unset === egmTypeCounts.total
                          ? 'Type is not populated yet — click “Populate types from DimMachine” above (or re-import DimMachine in Daily Data).'
                          : 'No machines match your search — try a different house #, name, type, or active filter.'}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {egms.length > 0 && (
                <p className="text-xs text-gray-500 mt-3">
                  {filteredEgms.length === egms.length
                    ? `${egms.length} machines (numbered 1–${egms.length})`
                    : `Showing ${filteredEgms.length} of ${egms.length} machines (rows 1–${filteredEgms.length} in this view)`}
                  {' · '}Daily DimMachine updates status/zone on matching house numbers · Last vendor sync shown per row in edit.
                </p>
              )}
            </InfoCard>

            {egmModalOpen && (
              <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                <div className="relative top-12 mx-auto p-5 border w-11/12 max-w-3xl shadow-lg rounded-md bg-white mb-12">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">{egmForm.isEdit ? 'Edit EGM' : 'Add EGM'}</h3>
                    <button onClick={()=>setEgmModalOpen(false)} className="text-gray-500 hover:text-gray-700">
                      <XCircleIcon className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">EGM ID (vendor / internal)</label>
                      <input className="input-field" value={egmForm.egm_id} disabled={egmForm.isEdit} onChange={(e)=>setEgmForm(prev=>({...prev, egm_id:e.target.value}))} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">House Number</label>
                      <input className="input-field" value={egmForm.house_number} onChange={(e)=>setEgmForm(prev=>({...prev, house_number:e.target.value}))} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Serial Number</label>
                      <input className="input-field font-mono" value={egmForm.serial_number} onChange={(e)=>setEgmForm(prev=>({...prev, serial_number:e.target.value}))} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Denomination</label>
                      <input className="input-field" type="number" step="0.01" value={egmForm.denom} onChange={(e)=>setEgmForm(prev=>({...prev, denom:e.target.value}))} />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs text-gray-600 mb-1">Game name</label>
                      <input className="input-field" value={egmForm.game_name} onChange={(e)=>setEgmForm(prev=>({...prev, game_name:e.target.value}))} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Zone</label>
                      <input className="input-field" value={egmForm.zone} onChange={(e)=>setEgmForm(prev=>({...prev, zone:e.target.value}))} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Area</label>
                      <input className="input-field" value={egmForm.area} onChange={(e)=>setEgmForm(prev=>({...prev, area:e.target.value}))} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Status</label>
                      <input className="input-field" value={egmForm.status} onChange={(e)=>setEgmForm(prev=>({...prev, status:e.target.value}))} placeholder="e.g. Active, Inactive" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Type</label>
                      <select
                        className="input-field"
                        value={egmForm.machine_class || ''}
                        onChange={(e) => setEgmForm((prev) => ({ ...prev, machine_class: e.target.value }))}
                      >
                        <option value="">—</option>
                        <option value="EGM">EGM (normal)</option>
                        <option value="MTGM">MTGM (multi)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Venue</label>
                      <SelectDropdown
                        className="w-full"
                        value={egmForm.venue_id}
                        onChange={async (e) => {
                          const vid = e.target.value
                          setEgmForm((prev) => ({ ...prev, venue_id: vid }))
                          await loadVenueCameras(vid)
                        }}
                        options={[
                          { value: '', label: 'Select venue' },
                          ...venuesDb.map((v) => ({ value: v.venue_id, label: v.name || v.venue_id })),
                        ]}
                        placeholder="Select venue"
                      />
                    </div>
                    <div className="flex items-center gap-2 md:col-span-2">
                      <input type="checkbox" checked={egmForm.active} onChange={(e)=>setEgmForm(prev=>({...prev, active:e.target.checked}))} />
                      <span className="text-sm">Active on floor</span>
                    </div>
                  </div>

                  <div className="mt-6 border-t pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-900">Camera mapping</h4>
                      <button
                        type="button"
                        className="text-xs text-blue-600 hover:underline"
                        onClick={() => setEgmForm((prev) => ({
                          ...prev,
                          cameras: [...prev.cameras, { camera_id: '', priority: prev.cameras.length + 1, notes: '' }],
                        }))}
                      >
                        + Add camera
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">Link one or more CCTV camera IDs for payout / incident “suggest cameras”.</p>
                    {egmForm.cameras.map((cam, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-end">
                        <div className="col-span-4">
                          <label className="block text-xs text-gray-500 mb-1">Camera ID</label>
                          <input
                            className="input-field font-mono"
                            list={`venue-cameras-${idx}`}
                            value={cam.camera_id}
                            onChange={(e) => {
                              const v = e.target.value
                              setEgmForm((prev) => {
                                const cameras = [...prev.cameras]
                                cameras[idx] = { ...cameras[idx], camera_id: v }
                                return { ...prev, cameras }
                              })
                            }}
                            placeholder="e.g. CAM-12"
                          />
                          <datalist id={`venue-cameras-${idx}`}>
                            {venueCameras.map((vc) => (
                              <option key={vc.camera_id} value={vc.camera_id}>{vc.name || vc.camera_id}</option>
                            ))}
                          </datalist>
                        </div>
                        <div className="col-span-2">
                          <label className="block text-xs text-gray-500 mb-1">Priority</label>
                          <input
                            type="number"
                            min={1}
                            className="input-field"
                            value={cam.priority}
                            onChange={(e) => {
                              const v = parseInt(e.target.value, 10) || 1
                              setEgmForm((prev) => {
                                const cameras = [...prev.cameras]
                                cameras[idx] = { ...cameras[idx], priority: v }
                                return { ...prev, cameras }
                              })
                            }}
                          />
                        </div>
                        <div className="col-span-5">
                          <label className="block text-xs text-gray-500 mb-1">Notes</label>
                          <input
                            className="input-field"
                            value={cam.notes || ''}
                            onChange={(e) => {
                              setEgmForm((prev) => {
                                const cameras = [...prev.cameras]
                                cameras[idx] = { ...cameras[idx], notes: e.target.value }
                                return { ...prev, cameras }
                              })
                            }}
                            placeholder="e.g. wide angle, faces screen"
                          />
                        </div>
                        <div className="col-span-1">
                          <button
                            type="button"
                            className="p-2 text-red-500 hover:bg-red-50 rounded"
                            onClick={() => setEgmForm((prev) => ({
                              ...prev,
                              cameras: prev.cameras.filter((_, i) => i !== idx),
                            }))}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-end gap-2 mt-5">
                    <button onClick={()=>setEgmModalOpen(false)} className="px-3 py-1.5 rounded-md bg-gray-200 text-gray-800 hover:bg-gray-300">Cancel</button>
                    <button
                      disabled={(!egmForm.egm_id && !egmForm.house_number) || savingEgm}
                      onClick={async ()=>{
                        try {
                          setSavingEgm(true)
                          const egmId = (egmForm.egm_id || egmForm.house_number || '').trim()
                          if (!egmId) throw new Error('EGM ID or house number required')
                          const payload = {
                            egm_id: egmId,
                            house_number: egmForm.house_number,
                            venue_id: egmForm.venue_id,
                            active: egmForm.active,
                            serial_number: egmForm.serial_number,
                            game_name: egmForm.game_name,
                            denom: egmForm.denom,
                            zone: egmForm.zone,
                            area: egmForm.area,
                            status: egmForm.status,
                            machine_class: egmForm.machine_class || null,
                          }
                          const method = egmForm.isEdit ? 'PUT' : 'POST'
                          const r = await authFetch(getLocalApiUrl('/api/egms'), {
                            method,
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(payload),
                          })
                          const j = await r.json()
                          if (!j.success) throw new Error(j.error || 'Save failed')

                          const cameras = egmForm.cameras.filter((c) => String(c.camera_id || '').trim())
                          if (cameras.length || egmForm.isEdit) {
                            await authFetch(getLocalApiUrl(`/api/egms/${encodeURIComponent(egmId)}/cameras`), {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ cameras }),
                            })
                          }

                          setEgmModalOpen(false)
                          await reloadEgms()
                        } catch (e) {
                          window.alert(e.message || 'Failed to save EGM')
                        } finally { setSavingEgm(false) }
                      }}
                      className="px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                    >Save</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Import Mapping Tab */}
        {selectedTab === 'import-mapping' && (
          <div className="space-y-6">
            <input
              ref={importMappingJsonInputRef}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={handleImportMappingJsonFileSelected}
            />
            <InfoCard title="Import Mapping" icon={ArrowUpTrayIcon}>
              <p className="text-sm text-gray-600 mb-4">
                Choose an import type, select the current pipe-delimited template, or create a new one.
                Column maps are edited in the New / Edit template dialog (not inline on this page).
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Import type</label>
                  <SelectDropdown
                    className="max-w-md"
                    value={importMappingType}
                    onChange={(e) => setImportMappingType(e.target.value)}
                    options={IMPORT_MAPPING_TYPE_OPTIONS}
                  />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => openNewTemplateModal()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm bg-blue-600 text-white hover:bg-blue-700"
                  >
                    <PlusIcon className="w-4 h-4" />
                    New template
                  </button>
                  <button
                    type="button"
                    onClick={triggerImportMappingJsonUpload}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border border-gray-300 bg-white hover:bg-gray-50"
                  >
                    <ArrowUpTrayIcon className="w-4 h-4" />
                    Import mapping JSON
                  </button>
                </div>

                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Preview template</div>
                      {importMappingLoading ? (
                        <p className="text-sm text-gray-500 mt-1">Loading…</p>
                      ) : (
                        <p className="text-base font-semibold text-gray-900 mt-1">
                          {selectedPreviewTemplate?.template_name || 'None selected'}
                        </p>
                      )}
                      {selectedPreviewTemplate?.gaming_machine_vendor && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          Vendor: {selectedPreviewTemplate.gaming_machine_vendor}
                          {selectedPreviewTemplate.type ? ` · ${selectedPreviewTemplate.type}` : ''}
                        </p>
                      )}
                      {importMappings[importMappingType]?.mappings && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          Delimiter: {sourceFormatLabel(importMappings[importMappingType]?.sourceFormat)}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        Tenant default: {selectedImportTemplate?.template_name || 'None'}
                        {previewMatchesDefault && selectedPreviewTemplate ? ' · matches preview' : ''}
                      </p>
                    </div>
                    <div className="max-w-xs w-full sm:w-64 space-y-2">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Select template (preview)</label>
                        <SelectDropdown
                          className="w-full"
                          value={importPreviewTemplateIds[importMappingType] || ''}
                          onChange={(e) => previewImportTemplate(e.target.value)}
                          options={[
                            { value: '', label: '— Select template —' },
                            ...importTemplates
                              .filter((t) => t.mapping_type === importMappingType)
                              .map((t) => ({ value: String(t.id), label: t.template_name })),
                          ]}
                          placeholder="— Select template —"
                        />
                      </div>
                      <button
                        type="button"
                        className="w-full inline-flex items-center justify-center px-3 py-1.5 rounded-md text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                        disabled={!importPreviewTemplateIds[importMappingType] || previewMatchesDefault || savingDefaultTemplate}
                        onClick={saveDefaultImportTemplate}
                      >
                        {savingDefaultTemplate ? 'Saving…' : 'Set as default template'}
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 pt-1 border-t border-gray-200">
                    <button
                      type="button"
                      className="inline-flex items-center px-3 py-1.5 rounded-md text-sm bg-white text-gray-800 border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                      disabled={!selectedPreviewTemplate && !(importMappings[importMappingType]?.mappings)}
                      onClick={downloadCurrentImportMappingJson}
                    >
                      <ArrowDownTrayIcon className="w-4 h-4 mr-1.5" />
                      Download JSON
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center px-3 py-1.5 rounded-md text-sm bg-white text-gray-800 border border-gray-300 hover:bg-gray-50"
                      onClick={() => downloadImportMappingSample(importMappingType)}
                    >
                      <ArrowDownTrayIcon className="w-4 h-4 mr-1.5" />
                      Download {importMappingSampleLabel(importMappingType)}
                    </button>
                    {importMappingType === 'egm' && (
                      <button
                        type="button"
                        className="inline-flex items-center px-3 py-1.5 rounded-md text-sm bg-white text-gray-800 border border-gray-300 hover:bg-gray-50"
                        onClick={() => downloadEgmColumnSchemaCsv()}
                      >
                        <ArrowDownTrayIcon className="w-4 h-4 mr-1.5" />
                        Column schema (CSV)
                      </button>
                    )}
                    <button
                      type="button"
                      className="inline-flex items-center px-3 py-1.5 rounded-md text-sm bg-white text-gray-800 border border-gray-300 hover:bg-gray-50"
                      onClick={() => setMappingPreviewOpen(true)}
                    >
                      Mapping preview
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center px-3 py-1.5 rounded-md text-sm bg-white text-blue-700 border border-blue-200 hover:bg-blue-50 disabled:opacity-50"
                      disabled={!importMappings[importMappingType]?.mappings}
                      onClick={openEditTemplateModal}
                    >
                      <PencilIcon className="w-4 h-4 mr-1.5" />
                      Edit template
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Preview loads mapping into the panel below. Use Set as default template to choose which template this tenant uses for scheduled ingest.
                    Downloads use the preview mapping. Format sample (.txt) is example pipe data only.
                  </p>

                  <div className="pt-2 border-t border-gray-200 space-y-2">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Loaded mapping (read-only)
                    </div>
                    <ImportMappingReadonlyTable
                      mappingType={importMappingType}
                      cfg={importMappings[importMappingType]}
                    />
                  </div>
                </div>
              </div>
            </InfoCard>

            <ImportMappingPreviewModal
              open={mappingPreviewOpen}
              onClose={() => setMappingPreviewOpen(false)}
              mappingType={importMappingType}
              templates={importTemplates}
              currentContent={importMappings[importMappingType]}
            />
            <ImportMappingTemplateModal
              open={templateModalOpen}
              mode={templateModalMode}
              mappingType={importMappingType}
              draft={modalDraft}
              onDraftChange={setModalDraft}
              templateName={saveTemplateName}
              onTemplateNameChange={setSaveTemplateName}
              templateType={saveTemplateType}
              onTemplateTypeChange={setSaveTemplateType}
              vendor={saveTemplateGamingMachineVendor}
              onVendorChange={setSaveTemplateGamingMachineVendor}
              saving={savingImportMapping}
              onClose={() => setTemplateModalOpen(false)}
              onSave={saveTemplateFromModal}
              onImportJson={triggerImportMappingJsonUpload}
            />
          </div>
        )}

        {/* Serial Numbers Tab */}
        {selectedTab === 'serial-numbers' && (
          <div className="space-y-6">
            <InfoCard title="Serial Number Configuration" icon={HashtagIcon}>
              <p className="text-sm text-gray-600 mb-4">
                Configure human-readable IDs per entity (Sprint 1 — design:{' '}
                <code className="text-xs bg-gray-100 px-1 rounded">docs/SERIAL_NUMBER_SYSTEM.md</code>
                ). Every pattern must include <code className="text-xs bg-gray-100 px-1 rounded">{'{seq:N}'}</code>.
                If the table below is empty, this tenant database may be missing pending SQL under{' '}
                <code className="text-xs bg-gray-100 px-1 rounded">backend/database/tenant-migrations/</code>
                (including <code className="text-xs bg-gray-100 px-1 rounded">032_serial_number_system_pg.sql</code>
                ). Use the button to apply all pending migrations for <strong>this</strong> tenant, or run{' '}
                <code className="text-xs bg-gray-100 px-1 rounded">node scripts/migrate_tenant_dbs.js --tenant &lt;groupId&gt;</code>{' '}
                from the backend folder.
              </p>
              {serialNumbersLoading ? (
                <div className="text-center py-8 text-gray-500">Loading serial numbers...</div>
              ) : serialNumbers.length === 0 ? (
                <div className="text-center py-8 text-gray-500 space-y-4">
                  <p>No serial number configurations found for this tenant.</p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <button
                      type="button"
                      onClick={handleApplySerialTenantMigrations}
                      disabled={serialMigrateLoading}
                      className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {serialMigrateLoading ? 'Applying migrations…' : 'Apply pending tenant migrations'}
                    </button>
                  </div>
                  {serialMigrateMessage && (
                    <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2 max-w-xl mx-auto">
                      {serialMigrateMessage}
                    </p>
                  )}
                  {serialMigrateError && (
                    <p className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-md px-3 py-2 max-w-xl mx-auto text-left">
                      {serialMigrateError}
                    </p>
                  )}
                  <p className="text-sm max-w-xl mx-auto">
                    Or run:{' '}
                    <code className="bg-gray-100 px-1 rounded text-xs">
                      {`cd backend && node scripts/migrate_tenant_dbs.js --tenant <groupId>`}
                    </code>
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entity</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pattern</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reset</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scope</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Counter</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Next preview</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {serialNumbers.map((config, idx) => (
                        <tr key={config.entity_type || idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{config.entity_type}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 max-w-xs">{config.description || '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 font-mono">{config.pattern}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              config.reset_period === 'yearly' ? 'bg-blue-100 text-blue-800' :
                              config.reset_period === 'daily' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {config.reset_period}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">{config.scope || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 font-mono">{config.current_value}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 font-mono">{config.next_preview || '-'}</td>
                          <td className="px-4 py-3 text-sm">
                            <button
                              onClick={() => handleSerialEdit(config)}
                              className="flex items-center px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                              <PencilIcon className="w-3 h-3 mr-1" />
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </InfoCard>

            {/* Serial Number Edit Modal */}
            {serialEditModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Edit serial number — {serialEditModal.entity_type}
                    </h3>
                    {serialEditModal.description && (
                      <p className="text-sm text-gray-600 mt-1">{serialEditModal.description}</p>
                    )}
                  </div>
                  <div className="p-6 space-y-4">
                    {serialForm.pattern !== (serialEditModal.pattern || '') && (
                      <div className="flex gap-2 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                        <ExclamationTriangleIcon className="w-5 h-5 shrink-0 text-amber-600" aria-hidden />
                        <span>
                          Changing the pattern only affects <strong>new</strong> numbers. Existing issued IDs are not renumbered.
                        </span>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Pattern</label>
                      <input
                        type="text"
                        value={serialForm.pattern}
                        onChange={(e) => handleSerialFormChange('pattern', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                        placeholder={'INV-{YYYY}-{seq:5}'}
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Required: <code className="bg-gray-100 px-0.5 rounded">{'{seq:N}'}</code> (zero-padded counter). Optional:{' '}
                        <code className="bg-gray-100 px-0.5 rounded">{'{YYYY}'}</code>,{' '}
                        <code className="bg-gray-100 px-0.5 rounded">{'{YY}'}</code>,{' '}
                        <code className="bg-gray-100 px-0.5 rounded">{'{MM}'}</code>,{' '}
                        <code className="bg-gray-100 px-0.5 rounded">{'{DD}'}</code>,{' '}
                        <code className="bg-gray-100 px-0.5 rounded">{'{YYMMDD}'}</code>,{' '}
                        <code className="bg-gray-100 px-0.5 rounded">{'{venue}'}</code>.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Reset period</label>
                      <SelectDropdown
                        value={serialForm.reset_period}
                        onChange={(e) => handleSerialFormChange('reset_period', e.target.value)}
                        className="w-full"
                        options={[
                          { value: 'yearly', label: 'Yearly (counter scope resets each calendar year)' },
                          { value: 'daily', label: 'Daily (counter scope resets each day)' },
                          { value: 'never', label: 'Never (single global counter)' },
                        ]}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Current counter value</label>
                      <input
                        type="number"
                        min="0"
                        value={serialForm.current_value}
                        onChange={(e) => handleSerialFormChange('current_value', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Next issued number uses this value + 1 for the current scope (year / day / global). Use this to continue from a legacy sequence.
                      </p>
                    </div>
                    {serialEditModal.scope === 'venue' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Sample venue code (preview only)</label>
                        <input
                          type="text"
                          value={serialPreviewVenueCode}
                          onChange={(e) => setSerialPreviewVenueCode(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono uppercase"
                          placeholder="e.g. WT"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Venue-scoped IDs use per-venue counters at runtime. Enter a venue code to preview <code className="bg-gray-100 px-0.5 rounded">{'{venue}'}</code> substitution.
                        </p>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Next number preview</label>
                      <div className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700 font-mono">
                        {serialPreview || '—'}
                      </div>
                    </div>
                    {serialSaveError && (
                      <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
                        {serialSaveError}
                      </div>
                    )}
                  </div>
                  <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-2">
                    <button
                      onClick={() => setSerialEditModal(null)}
                      className="flex items-center px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                    >
                      <XCircleIcon className="w-3 h-3 mr-1" />
                      Cancel
                    </button>
                    <button
                      onClick={handleSerialSave}
                      disabled={savingSerial}
                      className="flex items-center px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors disabled:opacity-50"
                    >
                      <CheckCircleIcon className="w-3 h-3 mr-1" />
                      {savingSerial ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {selectedTab === 'settings' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <InfoCard title="Default Transaction Limits" icon={CogIcon}>
                <div className="space-y-4">
                  {Object.entries(groupData.groupSettings.defaultLimits).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </span>
                      <span className="text-sm text-gray-900">${value.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </InfoCard>

              <InfoCard title="Compliance Settings" icon={CheckCircleIcon}>
                <div className="space-y-4">
                  {Object.entries(groupData.groupSettings.complianceSettings).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </span>
                      {typeof value === 'boolean' ? (
                        value ? (
                          <CheckCircleIcon className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircleIcon className="w-5 h-5 text-red-500" />
                        )
                      ) : (
                        <span className="text-sm text-gray-900">${value.toLocaleString()}</span>
                      )}
                    </div>
                  ))}
                </div>
              </InfoCard>

              <InfoCard title="Reporting Configuration" icon={GlobeAltIcon}>
                <div className="space-y-4">
                  {Object.entries(groupData.groupSettings.reportingConfig).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </span>
                      {value ? (
                        <CheckCircleIcon className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircleIcon className="w-5 h-5 text-red-500" />
                      )}
                    </div>
                  ))}
                </div>
              </InfoCard>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default GroupConfiguration